/** Help-system manifest: maps stable anchor ids to bundled markdown
 *  content. Every panel `?` button and every tool's "Help" entry
 *  passes one of the keys defined here.
 *
 *  Adding a new help section:
 *    1. Create a `.md` file under `help/panels/` or `help/tools/`.
 *    2. Add a `?raw` import below and an entry in `HELP_SECTIONS`.
 *    3. (Optional) link to the new section from `index.md`'s TOC.
 *
 *  The architecture-test `tests/help-manifest-coverage.test.ts`
 *  enforces 1:1 mapping between `.md` files and manifest entries —
 *  forgetting to register a new file or leaving a stale entry both
 *  fail the build. */

import indexMd from './index.md?raw';

// Panels
import propertiesMd from './panels/properties.md?raw';
import historyMd from './panels/history.md?raw';
import layersMd from './panels/layers.md?raw';

// Tools
import moveMd from './tools/move.md?raw';
import marqueeMd from './tools/marquee.md?raw';
import cropMd from './tools/crop.md?raw';
import drawMd from './tools/draw.md?raw';
import eraserMd from './tools/eraser.md?raw';
import textMd from './tools/text.md?raw';
import shapeMd from './tools/shape.md?raw';
import penMd from './tools/pen.md?raw';
import fillMd from './tools/fill.md?raw';
import retouchMd from './tools/retouch.md?raw';
import sliceMd from './tools/slice.md?raw';
import aiMd from './tools/ai.md?raw';
import transformMd from './tools/transform.md?raw';
import liquifyMd from './tools/liquify.md?raw';

/** All anchor ids the editor can deep-link to. Keep this union in
 *  sync with `HELP_SECTIONS` below — the TypeScript compiler enforces
 *  that every key in the record matches a union member. */
export type HelpAnchor =
  | 'index'
  | 'panels/properties'
  | 'panels/history'
  | 'panels/layers'
  | 'tools/move'
  | 'tools/marquee'
  | 'tools/crop'
  | 'tools/draw'
  | 'tools/eraser'
  | 'tools/text'
  | 'tools/shape'
  | 'tools/pen'
  | 'tools/fill'
  | 'tools/retouch'
  | 'tools/slice'
  | 'tools/ai'
  | 'tools/transform'
  | 'tools/liquify';

/** Stable section title for each anchor — used by the drawer's TOC,
 *  the deep-link label resolver, and analytics. Kept here rather
 *  than parsed from the markdown's H1 so renames don't accidentally
 *  break callsites. */
export const HELP_SECTION_TITLES: Record<HelpAnchor, string> = {
  index: 'Documentation',
  'panels/properties': 'Properties Panel',
  'panels/history': 'History Panel',
  'panels/layers': 'Layers Panel',
  'tools/move': 'Move Tool',
  'tools/marquee': 'Marquee Tool',
  'tools/crop': 'Crop Tool',
  'tools/draw': 'Brush Tool',
  'tools/eraser': 'Eraser Tool',
  'tools/text': 'Text Tool',
  'tools/shape': 'Shape Tool',
  'tools/pen': 'Pen Tool',
  'tools/fill': 'Fill Tool',
  'tools/retouch': 'Retouch Tool',
  'tools/slice': 'Slice Tool',
  'tools/ai': 'AI Tool',
  'tools/transform': 'Free Transform',
  'tools/liquify': 'Liquify',
};

export const HELP_SECTIONS: Record<HelpAnchor, string> = {
  index: indexMd,
  'panels/properties': propertiesMd,
  'panels/history': historyMd,
  'panels/layers': layersMd,
  'tools/move': moveMd,
  'tools/marquee': marqueeMd,
  'tools/crop': cropMd,
  'tools/draw': drawMd,
  'tools/eraser': eraserMd,
  'tools/text': textMd,
  'tools/shape': shapeMd,
  'tools/pen': penMd,
  'tools/fill': fillMd,
  'tools/retouch': retouchMd,
  'tools/slice': sliceMd,
  'tools/ai': aiMd,
  'tools/transform': transformMd,
  'tools/liquify': liquifyMd,
};

/** Map an active-tool id (e.g. 'retouch', 'ai') to the help anchor
 *  for that tool's docs. Used by the Properties panel's `?` button
 *  — clicking it opens help at the currently-active tool's section.
 *  Returns `null` for tools without a dedicated doc; callers fall
 *  back to the panel-level anchor. */
export function helpAnchorForTool(toolId: string): HelpAnchor | null {
  const candidate = `tools/${toolId}` as HelpAnchor;
  return candidate in HELP_SECTIONS ? candidate : null;
}

/** Ordered TOC entries grouped by category — drives the drawer's
 *  left rail. The order here is the order shown to the user. */
export const HELP_TOC: ReadonlyArray<{
  category: 'panels' | 'tools' | 'overview';
  label: string;
  entries: ReadonlyArray<{ anchor: HelpAnchor; label: string }>;
}> = [
  {
    category: 'overview',
    label: 'Overview',
    entries: [{ anchor: 'index', label: 'Introduction' }],
  },
  {
    category: 'panels',
    label: 'Panels',
    entries: [
      { anchor: 'panels/properties', label: 'Properties' },
      { anchor: 'panels/layers', label: 'Layers' },
      { anchor: 'panels/history', label: 'History' },
    ],
  },
  {
    category: 'tools',
    label: 'Tools',
    entries: [
      { anchor: 'tools/move', label: 'Move' },
      { anchor: 'tools/marquee', label: 'Marquee' },
      { anchor: 'tools/crop', label: 'Crop' },
      { anchor: 'tools/draw', label: 'Brush' },
      { anchor: 'tools/eraser', label: 'Eraser' },
      { anchor: 'tools/text', label: 'Text' },
      { anchor: 'tools/shape', label: 'Shape' },
      { anchor: 'tools/pen', label: 'Pen' },
      { anchor: 'tools/fill', label: 'Fill' },
      { anchor: 'tools/retouch', label: 'Retouch' },
      { anchor: 'tools/slice', label: 'Slice' },
      { anchor: 'tools/ai', label: 'AI' },
      { anchor: 'tools/transform', label: 'Free Transform' },
      { anchor: 'tools/liquify', label: 'Liquify' },
    ],
  },
];
